// Studio 3000 Main JavaScript

import headerImage from '../src/images/header.png';

const vertexShaderSource = `
    attribute vec2 a_position;
    void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
    }
`;

const fragmentShaderSource = `
    precision highp float;

    uniform vec2 u_resolution;
    uniform vec2 u_pointer;
    uniform float u_time;

    float hash(vec2 p) {
        p = fract(p * vec2(123.34, 345.45));
        p += dot(p, p + 34.345);
        return fract(p.x * p.y);
    }

    float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        return mix(
            mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
            mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0)), f.x),
            f.y
        );
    }

    float fbm(vec2 p) {
        float value = 0.0;
        float amplitude = 0.52;
        mat2 rotation = mat2(0.80, -0.60, 0.60, 0.80);
        for (int i = 0; i < 6; i++) {
            value += amplitude * noise(p);
            p = rotation * p * 2.03 + 19.1;
            amplitude *= 0.49;
        }
        return value;
    }

    void main() {
        vec2 uv = gl_FragCoord.xy / u_resolution.xy;
        vec2 p = uv - 0.5;
        p.x *= u_resolution.x / u_resolution.y;

        float t = u_time * 0.055;
        vec2 drift = vec2(t * 0.48, -t * 0.18);
        vec2 q = vec2(
            fbm(p * 1.22 + drift),
            fbm(p * 1.18 + vec2(4.6, 1.8) - drift * 0.7)
        );
        vec2 r = vec2(
            fbm(p * 1.72 + q * 2.25 + vec2(1.7, 7.2) + t),
            fbm(p * 1.58 + q * 2.05 + vec2(8.3, 2.8) - t * 0.7)
        );

        float cloud = fbm(p * 1.36 + r * 2.7 + q * 0.65);
        cloud = smoothstep(0.25, 0.92, cloud);

        vec2 pointer = u_pointer - 0.5;
        pointer.x *= u_resolution.x / u_resolution.y;
        float wake = exp(-length(p - pointer) * 3.2);
        cloud += wake * 0.10 * (0.5 + 0.5 * sin(u_time * 0.5));

        float centerGlow = exp(-length(p * vec2(0.72, 1.0)) * 1.4);
        vec3 blackBlue = vec3(0.003, 0.016, 0.038);
        vec3 deepBlue = vec3(0.01, 0.10, 0.23);
        vec3 electricBlue = vec3(0.01, 0.31, 0.61);
        vec3 color = mix(blackBlue, deepBlue, cloud * 1.25);
        color = mix(color, electricBlue, pow(cloud, 2.55) * (0.42 + centerGlow * 0.42));

        float grain = hash(gl_FragCoord.xy + fract(u_time) * 100.0) - 0.5;
        color += grain * 0.026;
        float vignette = 1.0 - smoothstep(0.35, 1.15, length(p * vec2(0.7, 0.9)));
        color *= 0.54 + vignette * 0.64;

        gl_FragColor = vec4(color, 1.0);
    }
`;

function createShader(gl, type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader);
        return null;
    }

    return shader;
}

function startSmoke(canvas) {
    const gl = canvas.getContext('webgl', {
        alpha: false,
        antialias: false,
        powerPreference: 'high-performance'
    });

    if (!gl) return;

    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
    if (!vertexShader || !fragmentShader) return;

    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
        gl.STATIC_DRAW
    );

    const position = gl.getAttribLocation(program, 'a_position');
    const resolution = gl.getUniformLocation(program, 'u_resolution');
    const pointerLocation = gl.getUniformLocation(program, 'u_pointer');
    const time = gl.getUniformLocation(program, 'u_time');
    const pointer = { x: 0.52, y: 0.48 };
    const pointerTarget = { ...pointer };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let lastFrame = 0;

    const resize = () => {
        const scale = Math.min(window.devicePixelRatio || 1, 1.5);
        const width = Math.round(canvas.clientWidth * scale);
        const height = Math.round(canvas.clientHeight * scale);
        if (canvas.width !== width || canvas.height !== height) {
            canvas.width = width;
            canvas.height = height;
            gl.viewport(0, 0, width, height);
        }
    };

    const updatePointer = (event) => {
        pointerTarget.x = event.clientX / window.innerWidth;
        pointerTarget.y = 1 - event.clientY / window.innerHeight;
    };

    window.addEventListener('pointermove', updatePointer, { passive: true });
    window.addEventListener('resize', resize, { passive: true });

    gl.useProgram(program);
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const render = (timestamp) => {
        if (reducedMotion && lastFrame) return;
        resize();
        pointer.x += (pointerTarget.x - pointer.x) * 0.035;
        pointer.y += (pointerTarget.y - pointer.y) * 0.035;
        gl.uniform2f(resolution, canvas.width, canvas.height);
        gl.uniform2f(pointerLocation, pointer.x, pointer.y);
        gl.uniform1f(time, timestamp * 0.001);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        lastFrame = timestamp;
        if (!reducedMotion) requestAnimationFrame(render);
    };

    requestAnimationFrame(render);
}

// DOM Content Loaded
document.addEventListener('DOMContentLoaded', function() {
    const smokeTexture = document.getElementById('smoke-texture');
    if (smokeTexture) smokeTexture.style.backgroundImage = `url(${headerImage})`;

    const smokeCanvas = document.getElementById('smoke-canvas');
    if (smokeCanvas) startSmoke(smokeCanvas);

});
